import * as React from 'react'
import loadable from '@loadable/component'

import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'
import { BreakpointArray } from '@thg-commerce/gravity-theme/breakpoints'

import { Control, ControlsContainer } from '../styles'
import {
  CarouselButtonPlacement,
  CarouselReducerActions,
  CarouselReducerActionTypes,
} from '../types'

const ChevronLeft = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronLeft'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const ChevronRight = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronRight'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const PauseIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Pause'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const PlayIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Play'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

interface ControlsProps {
  showControls: boolean
  autoPlay: boolean
  togglePause: () => void
  i18n: {
    playPauseLabel?: string
    leftScrollLabel?: string
    rightScrollLabel?: string
  }
  removeControlsOnSingleSlide?: boolean
  controls?: {
    visible?: BreakpointArray<boolean>
    size?: number
    alternativeChevronStyling?: boolean
  }
  controlPlacement?: CarouselButtonPlacement
  controlSize?: number
  enableWhiteControls?: boolean
  isPaused: boolean
  currentSlide: number
  totalSlides: number
  loopSlides: boolean
  onNavClick?: (direction: 'left' | 'right') => void
  dispatch: React.Dispatch<CarouselReducerActions>
  disabled: boolean
  previousSlideTo: React.MutableRefObject<number>
}

export const Controls: React.FC<ControlsProps> = ({
  showControls,
  removeControlsOnSingleSlide,
  autoPlay,
  togglePause,
  i18n,
  controls,
  controlPlacement,
  controlSize,
  enableWhiteControls,
  isPaused,
  currentSlide,
  totalSlides,
  loopSlides,
  onNavClick,
  dispatch,
  disabled,
  previousSlideTo,
}) => {
  return (
    <ControlsContainer
      hide={!showControls}
      removeControls={removeControlsOnSingleSlide && !showControls}
      controlPlacement={controlPlacement}
    >
      {autoPlay && (
        <Control
          disabled={false}
          onClick={togglePause}
          aria-label={i18n?.playPauseLabel}
          data-testid="carousel-play-button"
          visible={controls?.visible || [true]}
        >
          {isPaused ? (
            <PlayIcon width="24" height="24" />
          ) : (
            <PauseIcon width="24" height="24" />
          )}
        </Control>
      )}
      <Control
        position={HorizontalAlignment.LEFT}
        disabled={disabled}
        onClick={() => {
          previousSlideTo.current = previousSlideTo.current - 1
          dispatch({ type: CarouselReducerActionTypes.SlideLeft } as const)
          onNavClick?.('left')
        }}
        aria-label={i18n?.leftScrollLabel}
        data-testid="carousel-control-left"
        visible={controls?.visible || [true]}
        size={controls?.size}
        controlSize={controlSize}
        alternativeDisabledStyling={controls?.alternativeChevronStyling}
        enableWhiteControls={enableWhiteControls}
      >
        <ChevronLeft
          style={{ verticalAlign: 'middle' }}
          width="24"
          height="24"
        />
      </Control>

      <Control
        position={HorizontalAlignment.RIGHT}
        disabled={
          autoPlay || loopSlides ? false : currentSlide >= totalSlides - 1
        }
        onClick={() => {
          previousSlideTo.current = previousSlideTo.current + 1
          dispatch({ type: CarouselReducerActionTypes.SlideRight } as const)
          onNavClick?.('right')
        }}
        aria-label={i18n?.rightScrollLabel}
        data-testid="carousel-control-right"
        visible={controls?.visible || [true]}
        size={controls?.size}
        controlSize={controlSize}
        alternativeDisabledStyling={controls?.alternativeChevronStyling}
        enableWhiteControls={enableWhiteControls}
      >
        <ChevronRight
          style={{ verticalAlign: 'middle' }}
          width="24"
          height="24"
        />
      </Control>
    </ControlsContainer>
  )
}
